import type { EmailOtpType } from '@supabase/supabase-js';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

/**
 * Email confirmation landing page.
 *
 * Supabase's templates link here with `?token_hash=…&type=…`. ctrl-saas handled
 * this in a Next.js route handler that verified the token server-side and
 * redirected; a SPA can do it directly, because `verifyOtp` establishes the
 * session in the same client that will use it.
 *
 * Rendered outside the auth gate on purpose — there is no session yet, which is
 * the entire point of the visit.
 */
export const Route = createFileRoute('/auth/confirm')({
  validateSearch: (search: Record<string, unknown>) => ({
    token_hash: typeof search.token_hash === 'string' ? search.token_hash : undefined,
    type: typeof search.type === 'string' ? search.type : undefined,
    /** Where to land afterwards. Supabase templates send `callback`. */
    callback: typeof search.callback === 'string' ? search.callback : undefined,
    /**
     * Where to land afterwards, for links we mint ourselves — the invitation
     * flow sends `/join?invite_token=…`.
     */
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
  component: ConfirmPage,
});

function ConfirmPage() {
  const { token_hash, type, next } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  /**
   * React runs effects twice in StrictMode, and a token hash is single-use —
   * the second call would fail against an already-consumed token and show an
   * error on an otherwise successful confirmation.
   */
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token_hash || !type) {
      setError('This link is missing its confirmation token. Request a new email.');
      return;
    }

    supabase.auth
      .verifyOtp({ token_hash, type: type as EmailOtpType })
      .then(({ error: verifyError }) => {
        if (verifyError) {
          setError(verifyError.message);
          return;
        }

        // verifyOtp has established the session; the auth gate will now let us
        // through. Replace so Back does not return to a spent token.
        const destination = safeNext(next);

        if (destination) {
          router.history.replace(destination);
          return;
        }

        navigate({ to: '/', replace: true });
      })
      .catch((cause: Error) => setError(cause.message));
  }, [token_hash, type, next, navigate, router]);

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 text-center">
        {error ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight">Confirmation failed</h1>
            <p className="text-muted-foreground text-sm">{error}</p>
            <p className="text-muted-foreground text-xs">
              Confirmation links are single-use and must be opened in the same browser you signed up
              in.
            </p>
            <Button onClick={() => navigate({ to: '/', replace: true })}>Back to sign in</Button>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">Confirming your email…</p>
        )}
      </div>
    </div>
  );
}

/**
 * Only same-origin paths are followed.
 *
 * `next` arrives in a URL that lands on a page which has just established a
 * session, so an absolute value would be an open redirect straight into a
 * freshly-authenticated context. `//evil.example` is the case a bare
 * `startsWith('/')` misses: browsers read it as protocol-relative.
 */
function safeNext(next: string | undefined): string | null {
  if (!next?.startsWith('/') || next.startsWith('//')) return null;

  return next;
}
